import {useIsSafari} from '@github-ui/use-sticky-header/useIsSafari'
import {clsx} from 'clsx'
import {useLayoutEffect, useRef} from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {useCurrentLineHeight} from '../../../../hooks/use-current-line-height'
import type {HighlightPosition} from '../../../../utilities/lines'
import {
  calculateHighlightOffsetAndWidth,
  getActualLineNumberBinarySearch,
  queryLineElement,
} from '../../../../utilities/lines'
import {CopilotButton, type CopilotButtonHandle} from '../CopilotButton'
import {useHighlightedLinesRange} from './HighlightedLineProvider'
import styles from './HighlightedLinesOverlay.module.css'
import type {CodeLineData} from './hooks/use-code-lines'

export function HighlightedLinesOverlay({
  linesData,
  copilotAccessAllowed,
}: {
  linesData: CodeLineData[]
  copilotAccessAllowed: boolean
}) {
  const range = useHighlightedLinesRange()
  const {tabSize} = useCurrentBlob()
  const lineHeight = useCurrentLineHeight('react-line-numbers')
  //on safari the highlights are weirdly offset from the text area highlights, so we add a -3px bump to them
  const isSafari = useIsSafari()

  const anchorRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<CopilotButtonHandle>(null)

  const startLine = range ? getActualLineNumberBinarySearch(range.start.line, linesData) : 0
  const endLine = range?.end.line ? getActualLineNumberBinarySearch(range.end.line, linesData) : startLine

  const copilotButtonOffsetTop = isSafari ? startLine * lineHeight - 3 : startLine * lineHeight
  useLayoutEffect(() => {
    if (!range) return
    menuRef.current?.setAnchor(anchorRef.current)
  }, [range])
  if (!range) return null

  const highlightPositions: Array<{position: HighlightPosition; lineNumber: number}> = []

  for (let lineNumber = startLine; lineNumber <= endLine; lineNumber++) {
    const element = queryLineElement('', lineNumber + 1)
    if (!element) continue

    const position =
      calculateHighlightOffsetAndWidth(
        range,
        element,
        lineNumber + 1,
        tabSize,
        linesData[lineNumber + 1]?.rawText ?? '',
      ) ?? {}

    highlightPositions.push({position, lineNumber})
  }

  return (
    <div ref={anchorRef} style={{position: 'relative', top: copilotButtonOffsetTop + 10}}>
      {highlightPositions.map(({position, lineNumber}) => (
        <LineHighlight
          key={`line-${lineNumber}-highlight-${position.offset}`}
          highlightPosition={position}
          lineNumber={lineNumber}
          startingLineNumber={startLine}
        />
      ))}
      {copilotAccessAllowed && (
        <CopilotButton
          ref={menuRef}
          rowBeginNumber={range.start.line}
          rowEndNumber={range.end.line}
          recalcPosition={copilotButtonOffsetTop}
          id="code-line-copilot-button"
        />
      )}
    </div>
  )
}

export function LineHighlight({
  startingLineNumber,
  lineNumber,
  highlightPosition,
  subtle,
}: {
  startingLineNumber: number
  lineNumber: number
  highlightPosition?: HighlightPosition
  subtle?: boolean
}) {
  const lineHeight = useCurrentLineHeight('react-line-numbers')
  const topOffset = (lineNumber - startingLineNumber) * lineHeight
  return (
    <div
      className={clsx(
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        'line-highlight',
        subtle ? styles.lineHighlightSubtle : styles.lineHighlightActive,
        styles.lineHighlight,
      )}
      style={{
        height: lineHeight,
        left: highlightPosition && highlightPosition.offset ? `${highlightPosition.offset - 10}px` : '-82px',
        top: `${topOffset - 10}px`,
        width: highlightPosition && highlightPosition.width ? `${highlightPosition.width + 82}px` : 'calc(100% + 82px)',
      }}
      key={`highlighted-line-${lineNumber}`}
    />
  )
}
